import React, { useState, useEffect, useMemo } from 'react';
import { X, GitBranch, Database, Key, ChevronDown, ArrowRight, Link2 } from 'lucide-react';
import { db } from '../firebase'; // Assuming your firebase config is in this path
import { collection, getDocs } from 'firebase/firestore';

// --- Helper Functions ---

// Calculates a clean connection point on a node's border
const getEdgeConnectionPoint = (sourceNode, targetNode) => {
  const sx = sourceNode.x;
  const sy = sourceNode.y;
  const tx = targetNode.x;
  const ty = targetNode.y;

  const dx = tx - sx;
  const dy = ty - sy;

  if (dx === 0 && dy === 0) {
    return { sourceX: sx, sourceY: sy, targetX: tx, targetY: ty };
  }

  const angle = Math.atan2(dy, dx);
  const tanAngle = Math.tan(angle);

  const halfSourceWidth = sourceNode.width / 2;
  const halfSourceHeight = sourceNode.height / 2;
  const halfTargetWidth = targetNode.width / 2;
  const halfTargetHeight = targetNode.height / 2;

  let sourceX, sourceY, targetX, targetY;
  
  // Source point calculation
  const sourceRatio = halfSourceHeight / halfSourceWidth;
  if (Math.abs(tanAngle) < sourceRatio) {
    // Connect to vertical side (left/right)
    sourceX = sx + Math.sign(dx) * halfSourceWidth;
    sourceY = sy + Math.sign(dx) * halfSourceWidth * tanAngle;
  } else {
    // Connect to horizontal side (top/bottom)
    sourceY = sy + Math.sign(dy) * halfSourceHeight;
    sourceX = sx + Math.sign(dy) * halfSourceHeight / tanAngle;
  }

  // Target point calculation
  const targetRatio = halfTargetHeight / halfTargetWidth;
  if (Math.abs(tanAngle) < targetRatio) {
    // Connect to vertical side (left/right)
    targetX = tx - Math.sign(dx) * halfTargetWidth;
    targetY = ty - Math.sign(dx) * halfTargetWidth * tanAngle;
  } else {
    // Connect to horizontal side (top/bottom)
    targetY = ty - Math.sign(dy) * halfTargetHeight;
    targetX = tx - Math.sign(dy) * halfTargetHeight / tanAngle;
  }
  
  // Handle perfectly vertical lines where tan(angle) is Infinity
  if (!isFinite(sourceX)) sourceX = sx;
  if (!isFinite(targetX)) targetX = tx;

  return { sourceX, sourceY, targetX, targetY };
};

// Truncates text for display
const truncateText = (text, maxLength) => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

// Formats primary key for display
const formatPrimaryKey = (pk, isTruncated = true) => {
  if (!pk) return '';
  const text = Array.isArray(pk) ? `[${pk.join(', ')}]` : pk;
  return isTruncated ? truncateText(text, 15) : text;
};

// Formats foreign key for display
const formatForeignKey = (fk) => {
    const targetTable = fk.references?.table || fk.references.split('.')[0];
    const sourceCol = Array.isArray(fk.columns) ? `[${fk.columns.length}]` : fk.column;
    return `${truncateText(sourceCol, 8)} → ${truncateText(targetTable, 8)}`;
};

// Calculate smart tooltip position to keep it within bounds
const calculateTooltipPosition = (nodeX, nodeY, nodeWidth, nodeHeight, svgWidth, svgHeight, tooltipWidth = 250, tooltipHeight = 300) => {
  let x = nodeX + nodeWidth / 2 + 10;
  let y = nodeY - tooltipHeight / 2;

  // Adjust horizontal position if tooltip would overflow
  if (x + tooltipWidth > svgWidth - 10) {
    x = nodeX - nodeWidth / 2 - tooltipWidth - 10;
  }
  
  // Ensure tooltip doesn't go off left edge
  if (x < 10) {
    x = 10;
  }

  // Adjust vertical position if tooltip would overflow
  if (y < 10) {
    y = 10;
  } else if (y + tooltipHeight > svgHeight - 10) {
    y = svgHeight - tooltipHeight - 10;
  }

  return { x, y };
};

// Detailed Relationship Modal Component
const DetailedRelationshipModal = ({ table1, table2, onClose }) => {
  // Find relationships between the two tables
  const findRelationships = () => {
    const relationships = [];
    
    // Check foreign keys from table1 to table2
    table1.foreignKeys?.forEach(fk => {
      const targetTableName = fk.references?.table || (typeof fk.references === 'string' ? fk.references.split('.')[0] : null);
      if (targetTableName === table2.name) {
        const sourceColumns = fk.columns || [fk.column];
        const targetColumns = fk.references?.columns || [typeof fk.references === 'string' ? fk.references.split('.')[1] : ''];
        
        relationships.push({
          fromTable: table1.name,
          toTable: table2.name,
          fromColumns: sourceColumns,
          toColumns: targetColumns,
          type: fk.type || 'n:1',
          isFromFirst: true
        });
      }
    });

    // Check foreign keys from table2 to table1
    table2.foreignKeys?.forEach(fk => {
      const targetTableName = fk.references?.table || (typeof fk.references === 'string' ? fk.references.split('.')[0] : null);
      if (targetTableName === table1.name) {
        const sourceColumns = fk.columns || [fk.column];
        const targetColumns = fk.references?.columns || [typeof fk.references === 'string' ? fk.references.split('.')[1] : ''];
        
        relationships.push({
          fromTable: table2.name,
          toTable: table1.name,
          fromColumns: sourceColumns,
          toColumns: targetColumns,
          type: fk.type || 'n:1',
          isFromFirst: false
        });
      }
    });

    return relationships;
  };

  const relationships = findRelationships();

  const getTypeDescription = (type) => {
    switch (type) {
      case 'n:1': return 'Many to One';
      case '1:1': return 'One to One';
      case '1:n': return 'One to Many';
      case 'n:n': return 'Many to Many';
      default: return type;
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-xl font-semibold text-gray-900 flex items-center">
            <Link2 className="h-6 w-6 mr-2" />
            Detailed Relationship: {table1.name} ↔ {table2.name}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {relationships.length === 0 ? (
            <div className="text-center py-8">
              <Database className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 text-lg mb-2">No Direct Relationships Found</p>
              <p className="text-gray-400">
                These tables don't have any foreign key relationships between them.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Visual Diagram */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Relationship Diagram</h4>
                <div className="flex items-center justify-center space-x-8">
                  {/* Table 1 */}
                  <div className="bg-white border-2 border-blue-300 rounded-lg p-4 min-w-[200px]">
                    <div className="bg-blue-100 px-3 py-2 rounded-t-md -m-4 mb-3">
                      <h5 className="font-semibold text-blue-900">{table1.name}</h5>
                    </div>
                    <div className="space-y-1 text-sm">
                      {relationships
                        .filter(rel => rel.isFromFirst)
                        .map((rel, idx) => (
                          <div key={idx} className="text-purple-700 font-mono">
                            {rel.fromColumns.map(col => (
                              <div key={col} className="flex items-center">
                                <div className="w-2 h-2 bg-purple-600 rounded-full mr-2"></div>
                                {col}
                              </div>
                            ))}
                          </div>
                        ))}
                      {relationships
                        .filter(rel => !rel.isFromFirst)
                        .map((rel, idx) => (
                          <div key={idx} className="text-green-700 font-mono">
                            {rel.toColumns.map(col => (
                              <div key={col} className="flex items-center">
                                <div className="w-2 h-2 bg-green-600 rounded-full mr-2"></div>
                                {col}
                              </div>
                            ))}
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Arrow and relationship info */}
                  <div className="flex flex-col items-center space-y-2">
                    {relationships.map((rel, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <ArrowRight 
                          className={`h-6 w-6 ${rel.isFromFirst ? 'text-purple-600' : 'text-green-600 transform rotate-180'}`} 
                        />
                        <span className="text-xs bg-gray-200 px-2 py-1 rounded">
                          {getTypeDescription(rel.type)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Table 2 */}
                  <div className="bg-white border-2 border-orange-300 rounded-lg p-4 min-w-[200px]">
                    <div className="bg-orange-100 px-3 py-2 rounded-t-md -m-4 mb-3">
                      <h5 className="font-semibold text-orange-900">{table2.name}</h5>
                    </div>
                    <div className="space-y-1 text-sm">
                      {relationships
                        .filter(rel => !rel.isFromFirst)
                        .map((rel, idx) => (
                          <div key={idx} className="text-purple-700 font-mono">
                            {rel.fromColumns.map(col => (
                              <div key={col} className="flex items-center">
                                <div className="w-2 h-2 bg-purple-600 rounded-full mr-2"></div>
                                {col}
                              </div>
                            ))}
                          </div>
                        ))}
                      {relationships
                        .filter(rel => rel.isFromFirst)
                        .map((rel, idx) => (
                          <div key={idx} className="text-green-700 font-mono">
                            {rel.toColumns.map(col => (
                              <div key={col} className="flex items-center">
                                <div className="w-2 h-2 bg-green-600 rounded-full mr-2"></div>
                                {col}
                              </div>
                            ))}
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Detailed List */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Relationship Details</h4>
                <div className="space-y-4">
                  {relationships.map((rel, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">
                            {rel.fromTable} → {rel.toTable}
                          </span>
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {getTypeDescription(rel.type)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h6 className="text-sm font-medium text-gray-700 mb-2">From Columns ({rel.fromTable})</h6>
                          <div className="space-y-1">
                            {rel.fromColumns.map((col, colIdx) => (
                              <div key={colIdx} className="bg-purple-50 px-3 py-2 rounded text-sm font-mono text-purple-900">
                                {col}
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div>
                          <h6 className="text-sm font-medium text-gray-700 mb-2">To Columns ({rel.toTable})</h6>
                          <div className="space-y-1">
                            {rel.toColumns.map((col, colIdx) => (
                              <div key={colIdx} className="bg-green-50 px-3 py-2 rounded text-sm font-mono text-green-900">
                                {col}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Relationship Type Explanation */}
                      <div className="mt-3 p-3 bg-gray-50 rounded text-sm text-gray-600">
                        {rel.type === 'n:1' && (
                          <p><strong>Many to One:</strong> Multiple records in {rel.fromTable} can reference the same record in {rel.toTable}.</p>
                        )}
                        {rel.type === '1:1' && (
                          <p><strong>One to One:</strong> Each record in {rel.fromTable} references exactly one record in {rel.toTable}, and vice versa.</p>
                        )}
                        {rel.type === '1:n' && (
                          <p><strong>One to Many:</strong> Each record in {rel.fromTable} can be referenced by multiple records in {rel.toTable}.</p>
                        )}
                        {rel.type === 'n:n' && (
                          <p><strong>Many to Many:</strong> Multiple records in both tables can reference each other (typically through a junction table).</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Main Component ---

const RelationshipVisualization = ({ tables, onClose }) => {
  const [roles, setRoles] = useState([]);
  const [policies, setPolicies] = useState({});
  const [selectedRole, setSelectedRole] = useState('main_tables');
  const [loading, setLoading] = useState(true);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selectedTables, setSelectedTables] = useState([]);
  const [showDetailedRelationship, setShowDetailedRelationship] = useState(false);

  // Fetch roles and policies from Firestore
  useEffect(() => {
    const fetchRolesAndPolicies = async () => {
      setLoading(true);
      try {
        const rolesSnapshot = await getDocs(collection(db, 'roles'));
        const rolesData = rolesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setRoles(rolesData);

        const policiesSnapshot = await getDocs(collection(db, 'policies'));
        const policiesData = {};
        policiesSnapshot.forEach(doc => {
          policiesData[doc.id] = doc.data();
        });
        setPolicies(policiesData);
      } catch (error) {
        console.error('Error fetching roles and policies:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRolesAndPolicies();
  }, []);

  // Handle table selection
  const handleTableSelection = (tableId, isChecked) => {
    if (isChecked) {
      if (selectedTables.length < 2) {
        const newSelection = [...selectedTables, tableId];
        setSelectedTables(newSelection);
        
        // If 2 tables are selected, show detailed relationship
        if (newSelection.length === 2) {
          setShowDetailedRelationship(true);
        }
      }
    } else {
      setSelectedTables(selectedTables.filter(id => id !== tableId));
      setShowDetailedRelationship(false);
    }
  };

  // Filter tables based on the selected role
  const filteredTables = useMemo(() => {
    if (selectedRole === 'main_tables') {
      return tables.filter(table => !table.isMaterializedView);
    }

    const role = roles.find(r => r.id === selectedRole);
    if (!role || !role.policies || !Object.keys(policies).length) return [];

    const roleTableIds = new Set();
    role.policies.forEach(policyId => {
      const policy = policies[policyId];
      if (policy && policy.tables) {
        Object.values(policy.tables).forEach(tableConfig => {
          if (tableConfig.materializedViewId) {
            roleTableIds.add(tableConfig.materializedViewId);
          }
        });
      }
    });

    return tables.filter(table => roleTableIds.has(table.id));
  }, [selectedRole, tables, roles, policies]);

  // Build the graph structure (nodes and edges)
  const graph = useMemo(() => {
    const nodes = [];
    const edges = [];
    const nodeMap = new Map();

    if (filteredTables.length === 0) return { nodes, edges, nodeMap };

    const SVG_WIDTH = 1200, SVG_HEIGHT = 700, MARGIN = 50, NODE_WIDTH = 180, NODE_HEIGHT = 120;

    filteredTables.forEach((table) => {
        const node = {
            id: table.name, name: table.name, width: NODE_WIDTH, height: NODE_HEIGHT,
            primaryKey: table.primaryKey, foreignKeys: table.foreignKeys || [],
            incomingReferences: [], isMaterializedView: table.isMaterializedView,
            x: 0, y: 0
        };
        nodes.push(node);
        nodeMap.set(table.name, node);
    });

    nodes.forEach(sourceNode => {
      sourceNode.foreignKeys.forEach(fk => {
        const targetTableName = fk.references?.table || (typeof fk.references === 'string' ? fk.references.split('.')[0] : null);
        const targetNode = nodeMap.get(targetTableName);
        if (targetNode) {
          edges.push({
            id: `${sourceNode.id}->${targetNode.id}-${fk.column || fk.columns?.[0] || 'unknown'}`,
            source: sourceNode.id, target: targetNode.id,
            isComposite: Array.isArray(fk.columns) && fk.columns.length > 1,
          });

          const refCols = fk.references?.columns || (typeof fk.references === 'string' ? [fk.references.split('.')[1]] : ['?']);
          targetNode.incomingReferences.push({
            fromTable: sourceNode.name,
            fromColumn: fk.column || fk.columns?.join(', ') || '?',
            toColumn: refCols.join(', ')
          });
        }
      });
    });

    if (nodes.length > 0) {
        const cols = Math.min(Math.ceil(Math.sqrt(nodes.length)), 4);
        const rows = Math.ceil(nodes.length / cols);
        const xSpacing = (SVG_WIDTH - 2 * MARGIN) / cols;
        const ySpacing = (SVG_HEIGHT - 2 * MARGIN) / rows;
        nodes.forEach((node, index) => {
            const col = index % cols, row = Math.floor(index / cols);
            node.x = MARGIN + (col * xSpacing) + (xSpacing / 2);
            node.y = MARGIN + (row * ySpacing) + (ySpacing / 2);
        });
    }

    return { nodes, edges, nodeMap, SVG_WIDTH, SVG_HEIGHT };
  }, [filteredTables]);

  // Get selected table objects for detailed view
  const getSelectedTableObjects = () => {
    if (selectedTables.length !== 2) return [null, null];
    
    const table1 = filteredTables.find(t => t.name === selectedTables[0]);
    const table2 = filteredTables.find(t => t.name === selectedTables[1]);
    
    return [table1, table2];
  };

  const [selectedTable1, selectedTable2] = getSelectedTableObjects();

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-[95vw] w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center space-x-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                    <GitBranch className="h-6 w-6 mr-2" />
                    Table Relationships
                </h2>
                <div className="relative">
                    <select
                        value={selectedRole}
                        onChange={(e) => {
                          setSelectedRole(e.target.value);
                          setSelectedTables([]);
                          setShowDetailedRelationship(false);
                        }}
                        className="appearance-none bg-white border border-gray-300 rounded-md px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                        <option value="main_tables">Main Tables</option>
                        {roles.map(role => (
                          <option key={role.id} value={role.id}>
                            {role.name} (Role)
                          </option>
                        ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
                
                {/* Table Selection Info */}
                {filteredTables.length > 0 && (
                  <div className="text-sm text-gray-600">
                    Select up to 2 tables to view detailed relationships
                    {selectedTables.length > 0 && (
                      <span className="ml-2 text-primary">
                        ({selectedTables.length}/2 selected)
                      </span>
                    )}
                  </div>
                )}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <X className="h-6 w-6" />
            </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {graph.nodes.length === 0 ? (
            <div className="flex items-center justify-center h-full">
                <div className="text-gray-500 text-center">
                    <Database className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No tables found for this selection.</p>
                </div>
            </div>
          ) : (
            <div className="flex justify-center items-center min-h-full">
              <svg 
              width={graph.SVG_WIDTH} 
              height={graph.SVG_HEIGHT} 
              className="border border-gray-200 rounded-lg bg-gray-50/50 max-w-full"
              viewBox={`0 0 ${graph.SVG_WIDTH} ${graph.SVG_HEIGHT}`}
              >
              <defs>
                  <marker 
                    id="arrowhead" 
                    markerWidth="10" 
                    markerHeight="7" 
                    refX="9" 
                    refY="3.5" 
                    orient="auto"
                  >
                      <polygon points="0 0, 10 3.5, 0 7" fill="#6D28D9" />
                  </marker>
                  <marker 
                    id="arrowhead-red" 
                    markerWidth="10" 
                    markerHeight="7" 
                    refX="9" 
                    refY="3.5" 
                    orient="auto"
                  >
                      <polygon points="0 0, 10 3.5, 0 7" fill="#EF4444" />
                  </marker>
                  <filter id="dropshadow" x="-20%" y="-20%" width="140%" height="140%">
                      <feDropShadow dx="1" dy="2" stdDeviation="2" floodColor="#000000" floodOpacity="0.1"/>
                  </filter>
              </defs>

              {/* Nodes First */}
              {graph.nodes.map((node) => {
                  const halfWidth = node.width / 2, halfHeight = node.height / 2;
                  const isMV = node.isMaterializedView;
                  const isSelected = selectedTables.includes(node.id);
                  const borderColor = isSelected ? "#EF4444" : (isMV ? "#F59E0B" : "#3B82F6");
                  const headerColor = isSelected ? "#FEE2E2" : (isMV ? "#FEF3C7" : "#DBEAFE");

                  return (
                      <g key={node.id} transform={`translate(${node.x}, ${node.y})`} 
                        onMouseEnter={() => setHoveredNode(node)}
                        onMouseLeave={() => setHoveredNode(null)}
                        style={{ cursor: 'pointer' }}
                      >
                          {/* Selection Checkbox */}
                          <foreignObject x={-halfWidth - 15} y={-halfHeight - 15} width="30" height="30">
                            <div className="flex items-center justify-center">
                              <input
                                type="checkbox"
                                checked={selectedTables.includes(node.id)}
                                disabled={!selectedTables.includes(node.id) && selectedTables.length >= 2}
                                onChange={(e) => handleTableSelection(node.id, e.target.checked)}
                                className="form-checkbox h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                              />
                            </div>
                          </foreignObject>
                          
                          <rect x={-halfWidth} y={-halfHeight} width={node.width} height={node.height} rx="8" fill="white" stroke={borderColor} strokeWidth={isSelected ? "2.5" : "1.5"} filter="url(#dropshadow)" />
                          <rect x={-halfWidth} y={-halfHeight} width={node.width} height="32" rx="8" fill={headerColor} />
                          <line x1={-halfWidth} y1={-halfHeight + 32} x2={halfWidth} y2={-halfHeight+32} stroke={borderColor} strokeWidth="1" />
                          <text x="0" y={-halfHeight + 21} textAnchor="middle" className="font-semibold text-sm" fill={isSelected ? "#DC2626" : (isMV ? "#92400E" : "#1E40AF")}>
                              {truncateText(node.name, 20)}
                          </text>
                          <foreignObject x={-halfWidth + 10} y={-halfHeight + 40} width={node.width - 20} height={node.height - 45}>
                              <div className="space-y-1 text-xs text-gray-700 font-mono">
                                  {node.primaryKey && (<div className="flex items-center gap-1.5 truncate"><Key className="h-3 w-3 text-yellow-500 flex-shrink-0" /><span>PK: {formatPrimaryKey(node.primaryKey)}</span></div>)}
                                  {node.foreignKeys.map((fk, i) => (<div key={i} className="flex items-center gap-1.5 truncate"><div className="h-1.5 w-1.5 bg-purple-600 rounded-sm flex-shrink-0"/><span>FK: {formatForeignKey(fk)}</span></div>))}
                                  {node.incomingReferences.map((ref, i) => (<div key={i} className="flex items-center gap-1.5 truncate"><div className="h-1.5 w-1.5 bg-emerald-600 rounded-full flex-shrink-0"/><span>Ref: from {ref.fromTable}</span></div>))}
                              </div>
                          </foreignObject>
                      </g>
                  );
              })}

              {/* Edges Last (so arrowheads stay on top) */}
              {graph.edges.map((edge) => {
                  const sourceNode = graph.nodeMap.get(edge.source);
                  const targetNode = graph.nodeMap.get(edge.target);
                  if (!sourceNode || !targetNode) return null;
                  
                  const isHighlighted = selectedTables.includes(edge.source) && selectedTables.includes(edge.target);
                  const { sourceX, sourceY, targetX, targetY } = getEdgeConnectionPoint(sourceNode, targetNode);
                  
                  return (
                      <line
                          key={edge.id} 
                          x1={sourceX} 
                          y1={sourceY} 
                          x2={targetX} 
                          y2={targetY}
                          stroke={isHighlighted ? "#EF4444" : "#6D28D9"} 
                          strokeWidth={isHighlighted ? 3 : (edge.isComposite ? 2.5 : 1.5)}
                          strokeDasharray={edge.isComposite ? "5,3" : "none"}
                          markerEnd={isHighlighted ? "url(#arrowhead-red)" : "url(#arrowhead)"}
                      />
                  );
              })}

              {/* Hover Tooltip */}
              {hoveredNode && (() => {
                const tooltipPos = calculateTooltipPosition(
                  hoveredNode.x, 
                  hoveredNode.y, 
                  hoveredNode.width, 
                  hoveredNode.height, 
                  graph.SVG_WIDTH, 
                  graph.SVG_HEIGHT
                );
                
                return (
                  <g transform={`translate(${tooltipPos.x}, ${tooltipPos.y})`}>
                      <foreignObject x="0" y="0" width="250" height="300">
                       <div className="bg-gray-800 text-white rounded-lg p-3 shadow-lg text-xs font-mono break-words pointer-events-none max-h-[280px] overflow-y-auto">
                              <p className="font-bold text-sm mb-2">{hoveredNode.name}</p>
                              {hoveredNode.primaryKey && <p className="mb-1 text-yellow-300">PK: {formatPrimaryKey(hoveredNode.primaryKey, false)}</p>}
                              
                              {hoveredNode.foreignKeys.length > 0 && <div className="mt-2 pt-2 border-t border-gray-600">
                                  <p className="font-semibold text-purple-300">Foreign Keys:</p>
                                  {hoveredNode.foreignKeys.map((fk, i) => {
                                      const sourceCols = fk.columns || [fk.column];
                                      const refTable = fk.references?.table || fk.references.split('.')[0];
                                      const refCols = fk.references?.columns || [fk.references.split('.')[1]];
                                      return <p key={i}> {sourceCols.join(', ')} → {refTable}.({refCols.join(', ')})</p>
                                  })}
                              </div>}

                              {hoveredNode.incomingReferences.length > 0 && <div className="mt-2 pt-2 border-t border-gray-600">
                                  <p className="font-semibold text-emerald-300">Incoming Refs:</p>
                                  {hoveredNode.incomingReferences.map((ref, i) => (
                                      <p key={i}>{ref.toColumn} ← {ref.fromTable}.({ref.fromColumn})</p>
                                  ))}
                              </div>}
                          </div>
                      </foreignObject>
                  </g>
                );
              })()}
              </svg>
            </div>
          )}
        </div>
      </div>

      {/* Detailed Relationship Modal */}
      {showDetailedRelationship && selectedTable1 && selectedTable2 && (
        <DetailedRelationshipModal
          table1={selectedTable1}
          table2={selectedTable2}
          onClose={() => {
            setShowDetailedRelationship(false);
            setSelectedTables([]);
          }}
        />
      )}
    </div>
  );
};

export default RelationshipVisualization;